import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupeesCompact, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, RefreshCw, ShieldCheck, FileText, Receipt, Layers, TrendingUp } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEK   = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const KIND_ICON = { insurance: ShieldCheck, policy: FileText, license: Receipt, registration: Layers, other: Layers };

const EV_DOT = {
  expiry:   { cls: "bg-red-500",    label: "Expiry / renewal" },
  premium:  { cls: "bg-amber-500",  label: "Premium due" },
  maturity: { cls: "bg-emerald-500",label: "Maturity" },
};

/** Compliance calendar — 6-row month grid with colour-coded event dots. */
export default function ComplianceCalendar() {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() + 1 }); // 1-12
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const monthStr = `${cursor.y}-${String(cursor.m).padStart(2, "0")}`;

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/compliance/calendar?month=${monthStr}`);
      setData(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStr]);

  const cells = useMemo(() => {
    if (!data) return [];
    const first = new Date(cursor.y, cursor.m - 1, 1);
    // Monday-first calendar
    const dow = (first.getDay() + 6) % 7;   // 0 = Monday
    const last = new Date(cursor.y, cursor.m, 0).getDate();
    const grid = [];
    for (let i = 0; i < dow; i++) grid.push(null);
    for (let d = 1; d <= last; d++) {
      const iso = `${cursor.y}-${String(cursor.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      grid.push({ day: d, iso, events: data.events.filter((e) => e.date === iso) });
    }
    while (grid.length % 7 !== 0) grid.push(null);
    return grid;
  }, [data, cursor]);

  const step = (delta) => {
    let y = cursor.y, m = cursor.m + delta;
    if (m > 12) { m = 1; y += 1; }
    if (m < 1) { m = 12; y -= 1; }
    setCursor({ y, m });
  };

  const isToday = (iso) => iso === new Date().toISOString().slice(0, 10);

  const summary = useMemo(() => {
    if (!data) return { expiry: 0, premium: 0, maturity: 0, total: 0 };
    const s = { expiry: 0, premium: 0, maturity: 0, total: data.events.length };
    data.events.forEach((e) => { s[e.kind] = (s[e.kind] || 0) + 1; });
    return s;
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="calendar-title">
            The <em className="italic">renewal</em><br/>calendar.
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Every expiry, premium and maturity date on a single month grid. Click any dot to open the policy.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-md" disabled={busy} data-testid="refresh-cal">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Month controls + summary */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md">
          <button onClick={() => step(-1)} className="px-3 py-2 hover:bg-slate-50" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <div className="px-4 py-1.5 min-w-[160px] text-center">
            <div className="text-[10px] tracking-wider uppercase text-slate-400">Month</div>
            <div className="font-mono-fin font-semibold text-slate-900" data-testid="cal-month">{MONTHS[cursor.m - 1]} {cursor.y}</div>
          </div>
          <button onClick={() => step(1)} className="px-3 py-2 hover:bg-slate-50" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <button onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() + 1 })} className="text-[12px] text-slate-600 hover:text-red-600 inline-flex items-center gap-1.5" data-testid="cal-today">
          <CalIcon className="h-3.5 w-3.5" /> Today
        </button>
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          {Object.entries(EV_DOT).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${v.cls}`} />
              {v.label} <span className="font-mono-fin text-slate-800">{summary[k]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-[10px] tracking-wider uppercase text-slate-500">
          {WEEK.map((d) => <div key={d} className="p-2 text-center font-semibold">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((c, i) => (
            <div key={i} className="min-h-[100px] border-b border-r border-slate-100 last:border-r-0 p-1.5">
              {c ? (
                <div className={`h-full ${isToday(c.iso) ? "bg-red-50/50" : ""}`}>
                  <div className={`text-[11px] font-mono-fin ${isToday(c.iso) ? "text-red-700 font-bold" : "text-slate-500"}`}>{c.day}</div>
                  <div className="mt-1 space-y-1">
                    {c.events.slice(0, 3).map((e, k) => (
                      <button
                        key={`${e.policy_id}-${e.kind}-${k}`}
                        onClick={() => setDetail(e)}
                        data-testid={`cal-event-${e.policy_id}-${e.kind}`}
                        className={`w-full text-left text-[10px] px-1.5 py-1 rounded flex items-center gap-1.5 hover:brightness-95 ${
                          e.kind === "expiry" ? "bg-red-100 text-red-800" :
                          e.kind === "premium" ? "bg-amber-100 text-amber-900" :
                          "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${EV_DOT[e.kind].cls} flex-shrink-0`} />
                        <span className="truncate">{e.title}</span>
                      </button>
                    ))}
                    {c.events.length > 3 && (
                      <div className="text-[9px] text-slate-400 px-1">+{c.events.length - 3} more</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="opacity-30" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-md">
          {detail && <EventDetail e={detail} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EventDetail({ e, onClose }) {
  const KindIcon = KIND_ICON[e.policy_kind] || Layers;
  const evMeta = EV_DOT[e.kind];
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${evMeta.cls}`} />
          {evMeta.label}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-3">
          <KindIcon className="h-4 w-4 text-slate-500 mt-0.5" />
          <div className="min-w-0">
            <div className="font-medium text-slate-900">{e.title}</div>
            <div className="text-[11px] text-slate-500">{e.type_name || e.policy_kind}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[12px] pt-3 border-t border-slate-100">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Date</div>
            <div className="font-mono-fin mt-0.5">{formatDate(e.date)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Days from today</div>
            <div className={`font-mono-fin mt-0.5 ${e.days <= 15 ? "text-red-600" : "text-slate-700"}`}>
              {e.days < 0 ? `${Math.abs(e.days)}d overdue` : `${e.days}d`}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Amount</div>
            <div className="font-mono-fin mt-0.5">{formatRupeesCompact(e.amount || 0)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Status</div>
            <div className="font-mono-fin mt-0.5">{e.status}</div>
          </div>
        </div>
        <Link to="/compliance" onClick={onClose} className="inline-flex items-center gap-1.5 text-red-600 hover:text-red-700 text-[12px] pt-2">
          <TrendingUp className="h-3.5 w-3.5" /> Open in Compliance
        </Link>
      </div>
    </>
  );
}
